import { ref, computed } from 'vue'
import {
  ACCESS_TOKEN_HEADER,
  DEFAULT_OAUTH_PROVIDER,
  authServiceUrl,
  appSiteUrl,
  centralLoginUrl,
  signInEntryUrl,
} from '@/config/auth'

const { createSingleFlight, retryOnceOnUnauthorized } = require('@/utils/authRefresh')
const { clearWindForecastCache } = require('@/services/windForecastClient')

const TOKEN_KEY = 'pha_access_token'
const LEGACY_TOKEN_KEY = 'apiToken'

function isTokenCompatible (value) {
  if (!value || typeof value !== 'string') return false
  const trimmed = value.trim()
  if (!trimmed) return false
  // Reject legacy PHA JWTs — Paintbrush ID issues opaque tokens.
  if (trimmed.startsWith('eyJ')) return false
  if (trimmed.split('.').length === 3) return false
  return true
}

function readTokenFromStorage () {
  if (typeof window === 'undefined') return null
  return sessionStorage.getItem(TOKEN_KEY)
}

const token = ref(null)
const isLoggedIn = computed(() => !!token.value)

if (typeof window !== 'undefined') {
  const stored = readTokenFromStorage()
  token.value = isTokenCompatible(stored) ? stored : null
  localStorage.removeItem(LEGACY_TOKEN_KEY)
}

export function getStoredAccessToken () {
  const stored = readTokenFromStorage()
  if (!isTokenCompatible(stored)) {
    setStoredAccessToken(null)
    return null
  }
  token.value = stored
  return stored
}

const USER_ID_KEY = 'pha_user_id'

/** Signed-in user id remembered for this session so offline writes can be stamped. */
export function getStoredUserId () {
  if (typeof window === 'undefined') return ''
  return sessionStorage.getItem(USER_ID_KEY) || ''
}

export function setStoredUserId (value) {
  if (typeof window === 'undefined') return
  const id = typeof value === 'string' ? value.trim() : ''
  if (id) sessionStorage.setItem(USER_ID_KEY, id)
  else sessionStorage.removeItem(USER_ID_KEY)
}

export function setStoredAccessToken (value) {
  const compatible = isTokenCompatible(value) ? value : null
  // Signing out must not leave the previous user's id behind. A silent token refresh
  // keeps the same user, so it keeps the id.
  if (!compatible) setStoredUserId('')
  token.value = compatible
  if (typeof window === 'undefined') return
  if (compatible) sessionStorage.setItem(TOKEN_KEY, compatible)
  else sessionStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(LEGACY_TOKEN_KEY)
}

export function isLegacyJwtToken (value) {
  if (!value || typeof value !== 'string') return false
  const trimmed = value.trim()
  return trimmed.startsWith('eyJ') || trimmed.split('.').length === 3
}

function oauthUrl (path) {
  return authServiceUrl(path)
}

function authHeaders (extra = {}) {
  const t = getStoredAccessToken()
  if (!t) return { ...extra }
  return { ...extra, [ACCESS_TOKEN_HEADER]: t }
}

function loginUrl (provider, redirect = '/Map') {
  const path = typeof redirect === 'string' && redirect.startsWith('/') ? redirect : '/Map'
  return centralLoginUrl(provider, appSiteUrl(path))
}

function startLogin (provider, redirect = '/Map') {
  if (typeof window !== 'undefined') {
    window.location.href = loginUrl(provider, redirect)
  }
}

function extractTokenFromRedirectParam (redirectValue) {
  if (!redirectValue) return { token: null, cleaned: redirectValue }
  const q = redirectValue.indexOf('?')
  if (q < 0) return { token: null, cleaned: redirectValue }
  const params = new URLSearchParams(redirectValue.slice(q + 1))
  const found = params.get('accessToken')
  if (!found) return { token: null, cleaned: redirectValue }
  params.delete('accessToken')
  const path = redirectValue.slice(0, q)
  const search = params.toString()
  return { token: found, cleaned: search ? `${path}?${search}` : path }
}

function consumeAccessTokenFromUrl () {
  if (typeof window === 'undefined') return
  const url = new URL(window.location.href)
  let value = url.searchParams.get('accessToken')

  const rawHash = url.hash?.startsWith('#') ? url.hash.slice(1) : (url.hash || '')
  const hashParams = new URLSearchParams(rawHash)
  if (!value) value = hashParams.get('accessToken')

  const redirectParam = url.searchParams.get('redirect')
  if (!value && redirectParam) {
    const extracted = extractTokenFromRedirectParam(redirectParam)
    if (extracted.token) {
      value = extracted.token
      if (extracted.cleaned !== redirectParam) {
        if (extracted.cleaned) url.searchParams.set('redirect', extracted.cleaned)
        else url.searchParams.delete('redirect')
      }
    }
  }

  // Reject legacy PHA JWTs — bounce to Paintbrush ID instead of /login loop.
  if (value && isLegacyJwtToken(value)) {
    url.searchParams.delete('accessToken')
    hashParams.delete('accessToken')
    const hashString = hashParams.toString()
    const nextHash = hashString ? `#${hashString}` : ''
    const cleanPath = `${url.pathname}${url.search}${nextHash}` || '/Map'
    window.history.replaceState({}, '', cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`)
    window.location.replace(loginUrl(DEFAULT_OAUTH_PROVIDER, url.pathname || '/Map'))
    return
  }

  if (value) {
    // A token handed back by a sign-in may belong to a different user.
    if (value !== readTokenFromStorage()) setStoredUserId('')
    setStoredAccessToken(value)
  }

  const hadSearchToken = url.searchParams.has('accessToken')
  const hadHashToken = hashParams.has('accessToken')
  if (hadSearchToken) url.searchParams.delete('accessToken')
  if (hadHashToken) hashParams.delete('accessToken')

  if (hadSearchToken || hadHashToken) {
    const hashString = hashParams.toString()
    const nextHash = hashString ? `#${hashString}` : ''
    window.history.replaceState({}, '', `${url.pathname}${url.search}${nextHash}`)
  }
}

async function refreshAccessTokenOnce () {
  const response = await fetch(oauthUrl('/api/oauth/refresh'), {
    method: 'POST',
    credentials: 'include',
  })
  if (!response.ok) return null
  const data = await response.json()
  const next = data?.accessToken ?? null
  setStoredAccessToken(next)
  return next
}

const refreshAccessToken = createSingleFlight(refreshAccessTokenOnce)

export async function authFetch (input, init = {}) {
  const send = () => {
    const headers = authHeaders({ ...(init.headers || {}) })
    return fetch(input, { ...init, headers, credentials: 'include' })
  }
  const response = await retryOnceOnUnauthorized(send, refreshAccessToken)
  if (response.status === 401) handleUnauthorized()
  return response
}

async function checkAuth () {
  let current = getStoredAccessToken()
  if (!current) {
    try {
      current = await refreshAccessToken()
    } catch (e) {
      return false
    }
  }
  if (!current) return false

  try {
    const res = await fetch(oauthUrl('/api/oauth/me'), {
      headers: authHeaders(),
      credentials: 'include',
    })
    if (res.ok) return true
    if (res.status === 401) {
      const refreshed = await refreshAccessToken()
      if (refreshed) {
        const retry = await fetch(oauthUrl('/api/oauth/me'), {
          headers: authHeaders(),
          credentials: 'include',
        })
        if (retry.ok) return true
      }
      setStoredAccessToken(null)
    }
    return false
  } catch (e) {
    return true
  }
}

function handleUnauthorized () {
  setStoredAccessToken(null)
  if (typeof window === 'undefined') return false

  const path = window.location.pathname || ''
  if (path.startsWith('/login') || path.startsWith('/oauth-callback') || path.startsWith('/link-callback')) {
    return false
  }

  const dest = `${window.location.pathname}${window.location.search}${window.location.hash}` || '/Map'
  window.location.replace(`/login?reason=expired&redirect=${encodeURIComponent(dest)}`)
  return true
}

async function logout (redirect = '/') {
  try {
    await fetch(oauthUrl('/api/oauth/logout'), { method: 'POST', credentials: 'include' })
  } catch (e) {
    // ignore
  } finally {
    clearWindForecastCache()
    setStoredAccessToken(null)
    if (typeof window !== 'undefined') window.location.href = redirect
  }
}

export function useAuth () {
  return {
    token,
    isLoggedIn,
    loginUrl,
    startLogin,
    signInEntryUrl,
    consumeAccessTokenFromUrl,
    checkAuth,
    handleUnauthorized,
    authHeaders,
    logout,
    authFetch,
    setToken: setStoredAccessToken,
    getStoredAccessToken,
    getStoredUserId,
    setStoredUserId,
  }
}

export { consumeAccessTokenFromUrl, handleUnauthorized, authHeaders, checkAuth, signInEntryUrl }
